import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Search, Plus, Trash2, Pencil, X, Check,
  AlertCircle, AlertTriangle, RefreshCw, ShieldOff
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Mechanism {
  id: string;
  label: string;
  description: string;
  target_type: 'fixed' | 'lookup_material';
  allowed_values: string[] | null;
}

interface Entry {
  code: string;
  value: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CodeMappings() {
  const { user, getToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const canManage = String(user?.['custom:can_manage_mappings'] ?? '').toLowerCase() === 'true';

  const [mechanisms,    setMechanisms]    = useState<Mechanism[]>([]);
  const [activeMechId,  setActiveMechId]  = useState<string>('');
  const [entries,       setEntries]       = useState<Entry[]>([]);
  const [etag,          setEtag]          = useState<string>('');
  const [materialCodes, setMaterialCodes] = useState<string[]>([]);

  const [loadingMechs, setLoadingMechs] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [search,        setSearch]      = useState('');

  const [showForm,   setShowForm]   = useState(false);
  const [editingCode,setEditingCode]= useState<string | null>(null);
  const [formCode,   setFormCode]   = useState('');
  const [formValue,  setFormValue]  = useState('');
  const [saving,     setSaving]     = useState(false);
  const [formError,  setFormError]  = useState<string | null>(null);

  const activeMech = mechanisms.find(m => m.id === activeMechId);

  const callApi = useCallback(async (action: string, extra: Record<string, any> = {}) => {
    const token = await getToken();
    const res = await fetch(`${API_URL}/admin/mapping`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...extra }),
    });
    const result = await res.json();
    if (!res.ok) {
      throw new Error(result.error ?? `HTTP ${res.status}`);
    }
    return result;
  }, [API_URL, getToken]);

  // Load mechanism catalogue on mount
  useEffect(() => {
    if (!canManage) { setLoadingMechs(false); return; }
    const load = async () => {
      try {
        const result = await callApi('get_mechanisms');
        setMechanisms(result.mechanisms ?? []);
        if (result.mechanisms?.length) setActiveMechId(result.mechanisms[0].id);
      } catch (e: any) {
        setError(e.message ?? 'Failed to load mechanisms');
      } finally {
        setLoadingMechs(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  // Load entries whenever the active mechanism changes
  const loadEntries = useCallback(async (mechId: string) => {
    if (!mechId) return;
    setLoadingEntries(true);
    setError(null);
    setShowForm(false);
    try {
      const result = await callApi('get_mapping', { mechanism: mechId });
      setEntries(result.entries ?? []);
      setEtag(result.etag ?? '');
    } catch (e: any) {
      setError(e.message ?? 'Failed to load mappings');
    } finally {
      setLoadingEntries(false);
    }
  }, [callApi]);

  useEffect(() => {
    if (activeMechId) loadEntries(activeMechId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMechId]);

  // Load material codes lazily when that mechanism is selected
  useEffect(() => {
    if (activeMech?.target_type === 'lookup_material' && materialCodes.length === 0) {
      callApi('get_material_codes')
        .then(result => setMaterialCodes(result.material_codes ?? []))
        .catch(e => console.error('Failed to load material codes:', e));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMech]);

  const openAddForm = () => {
    setEditingCode(null);
    setFormCode('');
    setFormValue('');
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (entry: Entry) => {
    setEditingCode(entry.code);
    setFormCode(entry.code);
    setFormValue(entry.value);
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormError(null);
  };

  const saveEntry = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const result = await callApi('save_entry', {
        mechanism: activeMechId,
        code: formCode,
        value: formValue,
        etag,
      });
      setEntries(result.entries ?? []);
      setEtag(result.etag ?? '');
      setShowForm(false);
    } catch (e: any) {
      if (e.message?.includes('changed by someone else')) {
        setFormError(e.message);
        await loadEntries(activeMechId); // refresh to latest
      } else {
        setFormError(e.message ?? 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (code: string) => {
    if (!window.confirm(`Remove the mapping for "${code}"? Files already processed will not be affected, but this code will no longer translate on future uploads.`)) {
      return;
    }
    setError(null);
    try {
      const result = await callApi('delete_entry', { mechanism: activeMechId, code, etag });
      setEntries(result.entries ?? []);
      setEtag(result.etag ?? '');
    } catch (e: any) {
      if (e.message?.includes('changed by someone else')) {
        setError(e.message);
        await loadEntries(activeMechId);
      } else {
        setError(e.message ?? 'Failed to delete');
      }
    }
  };

  const filteredEntries = entries.filter(e =>
    !search ||
    e.code.toLowerCase().includes(search.toLowerCase()) ||
    e.value.toLowerCase().includes(search.toLowerCase())
  );

  // ── Access restricted ─────────────────────────────────────────────────────
  if (!canManage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <ShieldOff className="w-14 h-14 mx-auto mb-4 text-gray-300" />
          <h1 className="text-lg font-bold text-gray-900">Access Restricted</h1>
          <p className="text-sm text-gray-500 mt-2">
            You don't have permission to manage code mappings. Contact your account administrator if you believe this is an error.
          </p>
        </main>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        {/* Header */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-slate-700 rounded-lg">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Code Mappings</h1>
            <p className="text-sm text-gray-500">
              Manage how your company's codes translate to standard values used throughout Metrix
            </p>
          </div>
        </div>

        {loadingMechs ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
            <p className="text-sm">Loading…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

            {/* Mechanism list */}
            <div className="lg:col-span-1 space-y-1">
              {mechanisms.map(m => (
                <button
                  key={m.id}
                  onClick={() => setActiveMechId(m.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                    m.id === activeMechId
                      ? 'bg-slate-700 text-white font-medium'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Entries panel */}
            <div className="lg:col-span-3 space-y-4">

              {activeMech && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                  <p className="text-xs text-gray-500">{activeMech.description}</p>
                  {activeMech.target_type === 'fixed' && activeMech.allowed_values && (
                    <p className="text-xs text-gray-400 mt-1">
                      Standard values: {activeMech.allowed_values.join(', ')}
                    </p>
                  )}
                  {activeMech.target_type === 'lookup_material' && (
                    <p className="text-xs text-gray-400 mt-1">
                      Standard value must match an existing Metrix material code
                    </p>
                  )}
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search codes or values…"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>
                <button
                  onClick={openAddForm}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors flex-shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Mapping</span>
                </button>
              </div>

              {/* Add/Edit form */}
              {showForm && (
                <div className="bg-white rounded-xl border border-slate-300 shadow-sm p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {editingCode ? `Edit "${editingCode}"` : 'Add New Mapping'}
                    </h3>
                    <button onClick={closeForm}><X className="w-4 h-4 text-gray-400 hover:text-gray-600" /></button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Company Code</label>
                      <input
                        type="text"
                        value={formCode}
                        onChange={e => setFormCode(e.target.value)}
                        disabled={!!editingCode}
                        placeholder="e.g. RL"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Standard Value</label>
                      {activeMech?.target_type === 'fixed' ? (
                        <select
                          value={formValue}
                          onChange={e => setFormValue(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                        >
                          <option value="">Select…</option>
                          {activeMech.allowed_values?.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      ) : (
                        <select
                          value={formValue}
                          onChange={e => setFormValue(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                        >
                          <option value="">Select…</option>
                          {materialCodes.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      )}
                    </div>
                  </div>

                  {formError && (
                    <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-700">{formError}</p>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={saveEntry}
                      disabled={saving || !formCode.trim() || !formValue.trim()}
                      className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      <span>{saving ? 'Saving…' : 'Save'}</span>
                    </button>
                    <button
                      onClick={closeForm}
                      className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Table */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {loadingEntries ? (
                  <div className="text-center py-16 text-gray-400">
                    <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
                    <p className="text-sm">Loading mappings…</p>
                  </div>
                ) : filteredEntries.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <p className="text-sm">
                      {search ? 'No mappings match your search.' : 'No mappings defined yet for this mechanism.'}
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Company Code</th>
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Standard Value</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredEntries.map(entry => (
                        <tr key={entry.code} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{entry.code}</td>
                          <td className="px-4 py-2.5 text-gray-900">{entry.value}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-end space-x-1">
                              <button
                                onClick={() => openEditForm(entry)}
                                className="p-1.5 text-gray-400 hover:text-slate-700 hover:bg-gray-100 rounded transition-colors"
                                title="Edit"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => deleteEntry(entry.code)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remove"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex items-start space-x-2 text-xs text-gray-400">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <p>Changes apply to files uploaded going forward. Files already processed are not affected.</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
